import { ClickAwayListener } from "@mui/material";
import { FONT_FAMILIES } from "@redotech/redo-model/email-builder";
import * as classNames from "classnames";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { forwardRef, useEffect, useRef, useState } from "react";
import { EmojiPicker } from "../../emoji-picker";
import { quillFakeCursorPadding } from "../../quill/quill-editor";
import * as quillEditorCss from "./forge-quill-editor.module.css";

interface FormQuillEditorProps {
  selector: string;
  toolbarElementId: string;
  readOnly?: boolean;
  onTextChange?: (delta: any, oldDelta: any, source: string) => void;
  onSelectionChange?: (range: any, oldRange: any, source: string) => void;
  placeholder?: string;
}

export const FormQuillEditor = forwardRef<Quill | null, FormQuillEditorProps>(
  function FormQuillEditor(
    {
      selector,
      toolbarElementId = "toolbar",
      readOnly,
      onTextChange,
      onSelectionChange,
      placeholder,
    }: FormQuillEditorProps,
    ref,
  ) {
    const containerRef = useRef(null);

    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const emojiButtonRef = useRef<HTMLButtonElement | null>(null);

    const [cursorIndex, setCursorIndex] = useState(0);
    const quillRef = useRef<Quill | null>(null);

    useEffect(() => {
      const quill = new Quill(`#${selector}`, {
        placeholder: readOnly ? "" : placeholder || "",
        theme: "snow",
        modules: {
          toolbar: {
            container: `#${toolbarElementId}`,
            handlers: {
              "dynamic-variables": function (value: any) {
                const range = quill.getSelection();
                if (range) quill.insertText(range.index, value);
              },
              "emoji-picker": function () {
                setEmojiPickerOpen((open) => !open);
              },
            },
          },
          clipboard: { matchVisual: false },
        },
        formats: [
          "background",
          "bold",
          "color",
          "font",
          "code",
          "italic",
          "link",
          "size",
          "strike",
          "script",
          "underline",
          "blockquote",
          "header",
          "indent",
          "list",
          "align",
          "direction",
          "code-block",
        ],
      });
      quill.enable(!readOnly);
      quillRef.current = quill;

      const toolbarElement = document.querySelector(`#${toolbarElementId}`);
      const emojiButton = toolbarElement?.querySelector(`.ql-emoji-picker`);
      if (emojiButton) {
        emojiButtonRef.current = emojiButton as HTMLButtonElement;
      }

      // Pass Quill instance to parent via forwarded ref
      if (ref) {
        if (typeof ref === "function") {
          ref(quill);
        } else {
          (ref as React.MutableRefObject<Quill | null>).current = quill;
        }
      }

      return () => {
        quillRef.current?.disable();
        quillRef.current = null;
        if (ref) {
          if (typeof ref === "function") {
            ref(null);
          } else {
            (ref as React.MutableRefObject<Quill | null>).current = null;
          }
        }
      };
    }, [selector, toolbarElementId, readOnly, placeholder, ref]);

    useEffect(() => {
      if (!onTextChange || !quillRef.current) return;
      quillRef.current.on(Quill.events.TEXT_CHANGE, onTextChange);
      return () => {
        quillRef.current?.off(Quill.events.TEXT_CHANGE, onTextChange);
      };
    }, [onTextChange]);

    useEffect(() => {
      if (!onSelectionChange || !quillRef.current) return;
      quillRef.current.on(Quill.events.SELECTION_CHANGE, onSelectionChange);
      return () => {
        quillRef.current?.off(Quill.events.SELECTION_CHANGE, onSelectionChange);
      };
    }, [onSelectionChange]);

    useEffect(() => {
      const Font: any = Quill.import("formats/font");
      Font.whitelist = FONT_FAMILIES;
      Quill.register(Font, true);
    }, []);

    return (
      <>
        <div
          className={classNames(quillEditorCss.fakeCursor, {
            [quillEditorCss.active]: emojiPickerOpen,
          })}
          style={{ padding: `${quillFakeCursorPadding}px` }}
        />
        <ClickAwayListener onClickAway={() => setEmojiPickerOpen(false)}>
          <EmojiPicker
            anchor={emojiButtonRef.current}
            handleEmojiSelected={(emoji) => {
              if (!quillRef.current || !("native" in emoji)) return;
              const selection = quillRef.current.getSelection();
              const emojiText = emoji.native;

              if (selection && selection.length) {
                quillRef.current.deleteText(cursorIndex, selection.length);
              }
              quillRef.current.insertText(
                cursorIndex,
                emojiText,
                Quill.sources.USER,
              );

              const newCursorIndex = cursorIndex + emojiText.length;
              quillRef.current.setSelection(newCursorIndex, 0);
              setCursorIndex(newCursorIndex);
              setEmojiPickerOpen(false);
            }}
            onClose={() => setEmojiPickerOpen(false)}
            open={emojiPickerOpen}
            theme="light"
          />
        </ClickAwayListener>
        <div ref={containerRef} />
      </>
    );
  },
);
